import chunk from 'lodash-es/chunk';
import isNil from 'lodash-es/isNil';
import {
  SITE_PERFORMANCE_CHART_Y_AXIS_MAX_CHARS_PER_LINE,
  SITE_PERFORMANCE_CHART_BAR_HEIGHT,
  SITE_PERFORMANCE_CHART_INTRA_SITE_GAP,
  SITE_PERFORMANCE_CHART_INTER_SITE_GAP,
  SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE,
  SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO,
  SITE_PERFORMANCE_CHART_PAGE_SIZE,
} from '../constants/SitePerformanceChart.constants';
import { SiteWithOperatingTime } from 'app/modules/site-management/interfaces/Site.types';
import { MachinesListWithCleaningReport } from 'app/modules/machine-inventory/interfaces/Machine.types';
import { ReactChartUtils } from 'app/utils/react-charts/ReactChartUtils';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import {
  InputPeriod,
  SiteOperatingTimePeriod,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';

/** Sentinel id for the synthetic "no site assigned" bar — distinguishes it from real site ids sharing the same name. */
export const NO_SITE_ASSIGNED_BAR_ID = 'no-site-assigned';

export interface SitePerformanceChartData {
  label: string;
  data: SitePerformanceChartDatum[];
}

export interface SitePerformanceChartDatum {
  primary: string;
  secondary: number;
}

export class SitePerformanceChartUtils {
  public static sumNoSiteMachinesOperatingTime(
    machines: MachinesListWithCleaningReport['machines'],
    period: InputPeriod
  ): SiteOperatingTimePeriod {
    return (machines?.data ?? []).reduce(
      (totals, machine) => ({
        ...totals,
        actualTotalOperatingTimeMs:
          totals.actualTotalOperatingTimeMs + (machine.operatingTimeForPeriod?.actualTotalOperatingTimeMs ?? 0),
        plannedTotalOperatingTimeMs:
          totals.plannedTotalOperatingTimeMs + (machine.operatingTimeForPeriod?.plannedTotalOperatingTimeMs ?? 0),
      }),
      { actualTotalOperatingTimeMs: 0, plannedTotalOperatingTimeMs: 0, startAt: period.startAt, endAt: period.endAt }
    );
  }

  public static formatBarValue(ms: number): string {
    const [hours, minutes] = DateTime.formatDurationByMilliseconds({
      ms,
      showSeconds: false,
      padWithZeros: false,
    }).split(':');

    return `${hours}h ${minutes}m`;
  }

  private static truncateYAxisLine(text: string): string {
    return text.length > SITE_PERFORMANCE_CHART_Y_AXIS_MAX_CHARS_PER_LINE
      ? `${text.slice(0, SITE_PERFORMANCE_CHART_Y_AXIS_MAX_CHARS_PER_LINE - 3)}...`
      : text;
  }

  public static wrapYAxisLabel(label: string): string | string[] {
    const words = label.split(/\s+/).filter(Boolean);

    if (!words.length) {
      return '';
    }

    if (words.length === 1) {
      return SitePerformanceChartUtils.truncateYAxisLine(words[0]);
    }

    let firstLine = '';

    for (let index = 0; index < words.length; index += 1) {
      const candidate = firstLine ? `${firstLine} ${words[index]}` : words[index];

      if (candidate.length > SITE_PERFORMANCE_CHART_Y_AXIS_MAX_CHARS_PER_LINE && firstLine) {
        const remainder = words.slice(index).join(' ');

        return [
          SitePerformanceChartUtils.truncateYAxisLine(firstLine),
          SitePerformanceChartUtils.truncateYAxisLine(remainder),
        ];
      }

      firstLine = candidate;
    }

    return SitePerformanceChartUtils.truncateYAxisLine(firstLine);
  }

  public static getSiteRowHeight(hasPlannedSeries: boolean): number {
    return hasPlannedSeries
      ? SITE_PERFORMANCE_CHART_BAR_HEIGHT * 2 + SITE_PERFORMANCE_CHART_INTRA_SITE_GAP
      : SITE_PERFORMANCE_CHART_BAR_HEIGHT;
  }

  public static getGroupTop(siteIndex: number, chartAreaTop: number, hasPlannedSeries: boolean): number {
    return (
      chartAreaTop +
      siteIndex * (SitePerformanceChartUtils.getSiteRowHeight(hasPlannedSeries) + SITE_PERFORMANCE_CHART_INTER_SITE_GAP)
    );
  }

  public static getGroupCenterY(siteIndex: number, chartAreaTop: number, hasPlannedSeries: boolean): number {
    const groupTop = SitePerformanceChartUtils.getGroupTop(siteIndex, chartAreaTop, hasPlannedSeries);

    return groupTop + SitePerformanceChartUtils.getSiteRowHeight(hasPlannedSeries) / 2;
  }

  public static getBarCenterY(
    siteIndex: number,
    datasetIndex: number,
    chartAreaTop: number,
    hasPlannedSeries: boolean
  ): number {
    const groupTop = SitePerformanceChartUtils.getGroupTop(siteIndex, chartAreaTop, hasPlannedSeries);

    if (datasetIndex === 0) {
      return groupTop + SITE_PERFORMANCE_CHART_BAR_HEIGHT / 2;
    }

    return (
      groupTop +
      SITE_PERFORMANCE_CHART_BAR_HEIGHT +
      SITE_PERFORMANCE_CHART_INTRA_SITE_GAP +
      SITE_PERFORMANCE_CHART_BAR_HEIGHT / 2
    );
  }

  public static getPlotHeight(siteCount: number, hasPlannedSeries: boolean): number {
    if (siteCount <= 0) {
      return 0;
    }

    const siteRowHeight = hasPlannedSeries
      ? SITE_PERFORMANCE_CHART_BAR_HEIGHT * 2 + SITE_PERFORMANCE_CHART_INTRA_SITE_GAP
      : SITE_PERFORMANCE_CHART_BAR_HEIGHT;

    return siteCount * (siteRowHeight + 2 + SITE_PERFORMANCE_CHART_INTER_SITE_GAP * 1.5);
  }

  public static getContainerHeight(siteCount: number, hasPlannedSeries: boolean): number {
    return SitePerformanceChartUtils.getPlotHeight(siteCount, hasPlannedSeries);
  }

  public static getPageChartData(
    data: SitePerformanceChartData[],
    chartIndex: number,
    isGcd: boolean
  ): { actual: SitePerformanceChartDatum[]; planned: SitePerformanceChartDatum[] } {
    const actualPages = chunk(data[0].data, SITE_PERFORMANCE_CHART_PAGE_SIZE);
    const plannedPages = chunk(data[1]?.data ?? [], SITE_PERFORMANCE_CHART_PAGE_SIZE);

    return {
      actual: actualPages[chartIndex] ?? [],
      planned: isGcd ? plannedPages[chartIndex] ?? [] : [],
    };
  }

  public static convertSitesToChartData(
    sites: SiteWithOperatingTime[],
    noSiteMachinesOperatingTime?: SiteOperatingTimePeriod | null,
    noSiteMachinesLabel?: string
  ): SitePerformanceChartData[] {
    const data: SitePerformanceChartData[] = [
      { label: SITE_PERFORMANCE_CHART_COLUMN_LABEL_ONE, data: [] },
      { label: SITE_PERFORMANCE_CHART_COLUMN_LABEL_TWO, data: [] },
    ];

    const sitesWithOperatingTime = sites.filter(
      (site): site is SiteWithOperatingTime & { operatingTimeForPeriod: SiteOperatingTimePeriod } =>
        !isNil(site.operatingTimeForPeriod)
    );

    sitesWithOperatingTime.forEach(({ id, name, operatingTimeForPeriod }) => {
      const { actualTotalOperatingTimeMs, plannedTotalOperatingTimeMs } = operatingTimeForPeriod;

      data[0].data.push({
        primary: ReactChartUtils.combineColumnName(name, id),
        secondary: actualTotalOperatingTimeMs,
      });

      data[1].data.push({
        primary: ReactChartUtils.combineColumnName(name, id),
        secondary: plannedTotalOperatingTimeMs,
      });
    });

    if (noSiteMachinesOperatingTime && noSiteMachinesLabel) {
      const primary = ReactChartUtils.combineColumnName(noSiteMachinesLabel, NO_SITE_ASSIGNED_BAR_ID);

      data[0].data.push({ primary, secondary: noSiteMachinesOperatingTime.actualTotalOperatingTimeMs });
      data[1].data.push({ primary, secondary: noSiteMachinesOperatingTime.plannedTotalOperatingTimeMs });
    }

    return data;
  }
}
